import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { SocialAccountsPanel } from '@/components/social/SocialAccountsPanel';
import { PostStatusCard } from '@/components/social/PostStatusCard';
import { SchedulePostModal } from '@/components/social/SchedulePostModal';
import { VideoProjectsPanel } from '@/components/social/VideoProjectsPanel';
import {
  Calendar,
  List,
  Plus,
  Loader2,
  RefreshCw,
  Instagram,
  Youtube,
  Linkedin,
  Clock,
  CheckCircle,
  XCircle,
  Upload,
  Video,
  X,
  AlertTriangle,
} from 'lucide-react';

// TikTok icon component (not in lucide-react)
const TikTokIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);
import { useToast } from '@/hooks/use-toast';

interface PlatformStatus {
  platform: 'instagram' | 'youtube' | 'linkedin' | 'tiktok';
  accountId: string;
  status: 'pending' | 'scheduled' | 'posted' | 'failed';
  scheduledTime: string;
  postedAt?: string;
  platformPostId?: string;
  error?: string;
}

interface ScheduledPost {
  _id: string;
  projectId?: string;
  renderId?: string;
  videoUrl: string;
  thumbnailUrl?: string;
  platforms: PlatformStatus[];
  title: string;
  caption: string;
  tags?: string[];
  createdAt: string;
  updatedAt: string;
}

type FilterStatus = 'all' | 'pending' | 'scheduled' | 'posted' | 'failed' | 'missed';
type FilterPlatform = 'all' | 'instagram' | 'youtube' | 'linkedin' | 'tiktok';

const ScheduledPosts: React.FC = () => {
  const [posts, setPosts] = useState<ScheduledPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [filterPlatform, setFilterPlatform] = useState<FilterPlatform>('all');
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [editingPost, setEditingPost] = useState<ScheduledPost | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { toast } = useToast();

  // Video upload state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadedVideo, setUploadedVideo] = useState<{
    url: string;
    thumbnailUrl?: string;
    name: string;
    renderId?: string;
    s3Key?: string;
  } | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentProject, setCurrentProject] = useState<string>('default');
  const [libraryRefreshKey, setLibraryRefreshKey] = useState(0);

  // Initialize default project on mount
  useEffect(() => {
    try {
      // Ensure projects list exists with at least 'default'
      const savedProjects = localStorage.getItem('projects_NLE');
      let projectList = savedProjects ? JSON.parse(savedProjects) : [];
      if (!projectList.includes('default')) {
        projectList = ['default', ...projectList];
        localStorage.setItem('projects_NLE', JSON.stringify(projectList));
      }

      // Get or set current project
      const current = localStorage.getItem('currentProject_NLE');
      if (current && projectList.includes(current)) {
        setCurrentProject(current);
      } else {
        localStorage.setItem('currentProject_NLE', 'default');
        setCurrentProject('default');
      }
    } catch (e) {
      console.error('Error initializing projects:', e);
      setCurrentProject('default');
    }
  }, []);

  const fetchPosts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterPlatform !== 'all') params.append('platform', filterPlatform);

      const response = await fetch(`/api/scheduled-posts?${params.toString()}`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setPosts(data.posts || []);
      }
    } catch (error) {
      console.error('Failed to fetch scheduled posts:', error);
      toast({
        title: 'Error',
        description: 'Failed to load scheduled posts',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterPlatform, toast]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleVideoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('video/')) {
      toast({
        title: 'Invalid file',
        description: 'Please upload a video file',
        variant: 'destructive',
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Get presigned upload URL from backend
      const urlResponse = await fetch('/get-upload-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          fileName: file.name,
          fileType: file.type,
          bucket: 'hey-joe-videos', // Use permanent bucket for scheduled posts
        }),
      });

      if (!urlResponse.ok) {
        throw new Error('Failed to get upload URL');
      }

      const { uploadUrl, key, downloadUrl } = await urlResponse.json();
      // Backend returns downloadUrl (proxy URL for permanent storage)
      const videoUrl = downloadUrl.startsWith('/') ? `https://post.heyjoe.io${downloadUrl}` : downloadUrl;

      // Upload file to S3 with progress tracking
      const xhr = new XMLHttpRequest();

      await new Promise<void>((resolve, reject) => {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            setUploadProgress(Math.round((e.loaded / e.total) * 100));
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', () => reject(new Error('Upload failed')));
        xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));

        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });

      // Generate thumbnail from video (first frame)
      const thumbnailUrl = await generateVideoThumbnail(file);

      // Save to project library so it appears in Saved Library
      const videoEntry = {
        id: `upload_${Date.now()}`,
        name: file.name,
        url: videoUrl,
        savedS3Url: videoUrl,
        thumbnailUrl,
        timestamp: new Date().toISOString(),
        duration: 0, // Will be updated when video loads
        status: 'completed',
        s3Key: key,
      };

      try {
        // Save to localStorage as fallback
        const savedKey = `project_${currentProject}_savedRenders`;
        const existing = localStorage.getItem(savedKey);
        const savedVideos = existing ? JSON.parse(existing) : [];
        savedVideos.unshift(videoEntry); // Add to beginning
        localStorage.setItem(savedKey, JSON.stringify(savedVideos));

        // Also save to database if available
        try {
          const projectsRes = await fetch('/api/projects', { credentials: 'include' });
          if (projectsRes.ok) {
            const { projects } = await projectsRes.json();
            const dbProject = projects?.find((p: any) => p.name === currentProject);
            if (dbProject) {
              const existingSaved = dbProject.savedRenders || [];
              await fetch(`/api/projects/${dbProject._id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ savedRenders: [videoEntry, ...existingSaved] }),
              });
            }
          }
        } catch (dbErr) {
          console.warn('Could not save to database, localStorage only:', dbErr);
        }
      } catch (e) {
        console.error('Error saving video to library:', e);
      }

      setUploadedVideo({
        url: videoUrl,
        thumbnailUrl,
        name: file.name,
        s3Key: key,
      });

      toast({
        title: 'Upload complete',
        description: 'Your video has been saved to your library and is ready to schedule',
      });

      // Trigger refresh of the VideoProjectsPanel
      setLibraryRefreshKey(prev => prev + 1);
    } catch (error: any) {
      toast({
        title: 'Upload failed',
        description: error.message || 'Failed to upload video',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const generateVideoThumbnail = (file: File): Promise<string | undefined> => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      video.onloadedmetadata = () => {
        // Set canvas dimensions
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        video.currentTime = 0.1; // Get frame at 0.1 seconds
      };

      video.onseeked = () => {
        if (ctx) {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          try {
            const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.7);
            resolve(thumbnailUrl);
          } catch {
            resolve(undefined);
          }
        } else {
          resolve(undefined);
        }
        URL.revokeObjectURL(video.src);
      };

      video.onerror = () => {
        resolve(undefined);
        URL.revokeObjectURL(video.src);
      };

      video.src = URL.createObjectURL(file);
      video.load();
    });
  };

  const handleSchedule = async (data: {
    postId?: string;
    videoUrl: string;
    thumbnailUrl?: string;
    renderId?: string;
    title: string;
    caption: string;
    tags: string[];
    tiktokSettings?: {
      privacy_level: string;
      disable_comment: boolean;
      disable_duet: boolean;
      disable_stitch: boolean;
      brand_organic_toggle: boolean;
      brand_content_toggle: boolean;
    };
    platforms: {
      platform: 'instagram' | 'youtube' | 'linkedin' | 'tiktok';
      accountId: string;
      scheduledTime: string;
    }[];
  }) => {
    try {
      const method = data.postId ? 'PUT' : 'POST';
      const url = data.postId
        ? `/api/scheduled-posts/${data.postId}`
        : '/api/scheduled-posts';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      });

      if (response.ok) {
        toast({
          title: data.postId ? 'Post updated' : 'Post scheduled',
          description: data.postId
            ? 'Your scheduled post has been updated.'
            : 'Your post has been scheduled successfully.',
        });
        fetchPosts();
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to schedule post');
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to schedule post',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const handleDelete = async (postId: string) => {
    if (!confirm('Are you sure you want to delete this scheduled post?')) return;

    try {
      const response = await fetch(`/api/scheduled-posts/${postId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        toast({
          title: 'Post deleted',
          description: 'The scheduled post has been deleted.',
        });
        fetchPosts();
      } else {
        throw new Error('Failed to delete post');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete post',
        variant: 'destructive',
      });
    }
  };

  const handlePublishNow = async (postId: string) => {
    if (!confirm('Publish this post now?')) return;

    try {
      const response = await fetch(`/api/scheduled-posts/${postId}/publish-now`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        toast({
          title: 'Publishing',
          description: 'Your post is being published...',
        });
        fetchPosts();
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to publish');
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to publish post',
        variant: 'destructive',
      });
    }
  };

  const handleEdit = (post: ScheduledPost) => {
    setEditingPost(post);
    setIsModalOpen(true);
  };

  // Helper to determine effective status (handles "missed" for past scheduled posts)
  const getEffectiveStatus = (p: PlatformStatus): string => {
    if (p.status === 'scheduled' && new Date(p.scheduledTime) < new Date()) {
      return 'missed';
    }
    return p.status;
  };

  const getStatusCounts = () => {
    const counts = { pending: 0, scheduled: 0, posted: 0, failed: 0, missed: 0 };
    posts.forEach((post) => {
      post.platforms.forEach((p) => {
        const effectiveStatus = getEffectiveStatus(p);
        if (counts[effectiveStatus as keyof typeof counts] !== undefined) {
          counts[effectiveStatus as keyof typeof counts]++;
        }
      });
    });
    return counts;
  };

  const statusCounts = getStatusCounts();

  const filteredPosts = posts.filter((post) => {
    if (filterStatus !== 'all') {
      const hasStatus = post.platforms.some((p) => getEffectiveStatus(p) === filterStatus);
      if (!hasStatus) return false;
    }
    if (filterPlatform !== 'all') {
      const hasPlatform = post.platforms.some((p) => p.platform === filterPlatform);
      if (!hasPlatform) return false;
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-background">
      <main className="container mx-auto px-4 py-8">
        {/* Action buttons */}
        <div className="flex items-center justify-end gap-2 mb-6">
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            onChange={handleVideoUpload}
            className="hidden"
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
          >
            {isUploading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Upload className="h-4 w-4 mr-2" />
            )}
            Upload Video
          </Button>
          <Button onClick={() => setIsModalOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Schedule Post
          </Button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Video Projects from Enhancer */}
            <VideoProjectsPanel
              key={libraryRefreshKey}
              currentProject={currentProject}
              onProjectChange={(project) => {
                setCurrentProject(project);
              }}
              onSelectVideo={(video) => {
                setUploadedVideo({
                  url: video.url,
                  thumbnailUrl: video.thumbnailUrl,
                  name: video.name,
                  renderId: video.renderId,
                });
                setIsModalOpen(true);
              }}
            />

            {/* Upload Progress */}
            {isUploading && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Uploading Video
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Progress value={uploadProgress} className="mb-2" />
                  <p className="text-sm text-muted-foreground">{uploadProgress}% complete</p>
                </CardContent>
              </Card>
            )}

            {/* Uploaded Video Ready to Schedule */}
            {uploadedVideo && !isUploading && (
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Video className="h-4 w-4" />
                      Ready to Schedule
                    </CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setUploadedVideo(null)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {uploadedVideo.thumbnailUrl && (
                    <div className="aspect-video bg-muted rounded-lg overflow-hidden">
                      <img
                        src={uploadedVideo.thumbnailUrl}
                        alt={uploadedVideo.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <p className="text-sm font-medium truncate">{uploadedVideo.name}</p>
                  <Button
                    className="w-full"
                    onClick={() => setIsModalOpen(true)}
                  >
                    <Calendar className="h-4 w-4 mr-2" />
                    Schedule This Video
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Connected Accounts */}
            <SocialAccountsPanel onAccountsChange={fetchPosts} />

            {/* Status Overview */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Overview</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-blue-500" />
                    <span className="text-sm">Scheduled</span>
                  </div>
                  <span className="font-medium">{statusCounts.scheduled}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Posted</span>
                  </div>
                  <span className="font-medium">{statusCounts.posted}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <XCircle className="h-4 w-4 text-red-500" />
                    <span className="text-sm">Failed</span>
                  </div>
                  <span className="font-medium">{statusCounts.failed}</span>
                </div>
                {statusCounts.missed > 0 && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-orange-500" />
                      <span className="text-sm">Missed</span>
                    </div>
                    <span className="font-medium">{statusCounts.missed}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <CardTitle className="text-lg">Scheduled Posts</CardTitle>
                  <div className="flex items-center gap-2">
                    {/* View Mode Toggle */}
                    <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as 'list' | 'calendar')}>
                      <TabsList className="h-9">
                        <TabsTrigger value="list" className="px-3">
                          <List className="h-4 w-4" />
                        </TabsTrigger>
                        <TabsTrigger value="calendar" className="px-3">
                          <Calendar className="h-4 w-4" />
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>

                    {/* Filters */}
                    <Select
                      value={filterStatus}
                      onValueChange={(v) => setFilterStatus(v as FilterStatus)}
                    >
                      <SelectTrigger className="w-[130px] h-9">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="scheduled">Scheduled</SelectItem>
                        <SelectItem value="posted">Posted</SelectItem>
                        <SelectItem value="failed">Failed</SelectItem>
                        <SelectItem value="missed">Missed</SelectItem>
                      </SelectContent>
                    </Select>

                    <Select
                      value={filterPlatform}
                      onValueChange={(v) => setFilterPlatform(v as FilterPlatform)}
                    >
                      <SelectTrigger className="w-[130px] h-9">
                        <SelectValue placeholder="Platform" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Platforms</SelectItem>
                        <SelectItem value="instagram">
                          <span className="flex items-center gap-2">
                            <Instagram className="h-4 w-4" />
                            Instagram
                          </span>
                        </SelectItem>
                        <SelectItem value="youtube">
                          <span className="flex items-center gap-2">
                            <Youtube className="h-4 w-4" />
                            YouTube
                          </span>
                        </SelectItem>
                        <SelectItem value="linkedin">
                          <span className="flex items-center gap-2">
                            <Linkedin className="h-4 w-4" />
                            LinkedIn
                          </span>
                        </SelectItem>
                        <SelectItem value="tiktok">
                          <span className="flex items-center gap-2">
                            <TikTokIcon className="h-4 w-4" />
                            TikTok
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={fetchPosts}
                      className="h-9 w-9 p-0"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : filteredPosts.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-muted-foreground mb-4">
                      No scheduled posts found
                    </p>
                    <Button onClick={() => setIsModalOpen(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Schedule Your First Post
                    </Button>
                  </div>
                ) : viewMode === 'list' ? (
                  <div className="space-y-4">
                    {filteredPosts.map((post) => (
                      <PostStatusCard
                        key={post._id}
                        post={post}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onPublishNow={handlePublishNow}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    Calendar view coming soon
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Schedule Modal */}
      <SchedulePostModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingPost(null);
        }}
        onSchedule={async (data) => {
          await handleSchedule(data);
          // Clear uploaded video after successful scheduling
          setUploadedVideo(null);
        }}
        existingPost={editingPost ? {
          _id: editingPost._id,
          title: editingPost.title,
          caption: editingPost.caption,
          tags: editingPost.tags,
          platforms: editingPost.platforms,
        } : undefined}
        videoUrl={editingPost?.videoUrl || uploadedVideo?.url || undefined}
        thumbnailUrl={editingPost?.thumbnailUrl || uploadedVideo?.thumbnailUrl || undefined}
        renderId={uploadedVideo?.renderId}
      />
    </div>
  );
};

export default ScheduledPosts;
